"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";
import { FileManagerModal } from "@/components/fileManager/FileManagerModal";
import { FileManagerConfig, FileSystemItem } from "@/types/fileManager";

interface FileManagerContextType {
  openFileManager: (config: FileManagerConfig) => void;
  closeFileManager: () => void;
  isOpen: boolean;
}

const FileManagerContext = createContext<FileManagerContextType | undefined>(
  undefined,
);

interface FileManagerProviderProps {
  children: ReactNode;
}

export const FileManagerProvider: React.FC<FileManagerProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<FileManagerConfig | null>(null);

  const openFileManager = (config: FileManagerConfig) => {
    setConfig(config);
    setIsOpen(true);
  };

  const closeFileManager = () => {
    setIsOpen(false);
    setConfig(null);
  };

  return (
    <FileManagerContext.Provider
      value={{
        openFileManager,
        closeFileManager,
        isOpen,
      }}
    >
      {children}
      {isOpen && config && (
        <FileManagerModal
          isOpen={isOpen}
          onClose={closeFileManager}
          config={{
            ...config,
            onSelect: (files: FileSystemItem[]) => {
              config.onSelect(files);
              closeFileManager();
            },
            onCancel: () => {
              if (config.onCancel) {
                config.onCancel();
              }
              closeFileManager();
            },
          }}
        />
      )}
    </FileManagerContext.Provider>
  );
};

export const useFileManagerModal = () => {
  const context = useContext(FileManagerContext);
  if (context === undefined) {
    throw new Error(
      "useFileManagerModal must be used within a FileManagerProvider",
    );
  }
  return context;
};
